import React from 'react';
import { FieldGroup } from '@ver-uds/react';
import { Account, AccountType, MouTemplateType } from 'eve-account-client';
import { useAuthState } from '@vis-auth/react';
import Section from '../Section';
import { AuthFileLinkProps } from '../AuthFileLink';
import CompanyAccessMOU from './CompanyAccessMOU/CompanyAccessMOU';
import CompanyAccessMyI9 from './CompanyAccessMyI9/CompanyAccessMyI9';
import './CompanyAccess.css';

export type MouLinkType = MouTemplateType | string;

export interface CompanyAccessProps {
  accountData?: Pick<
    Account,
    'selfVerification' | 'type' | 'companyInfo' | 'webServiceAccess' | 'id' | 'mouSignedDate' | 'myI9'
  >;
  linkToMou?: React.ReactElement<AuthFileLinkProps<MouLinkType>>;
  linkToMyI9TOS?: React.ReactElement<AuthFileLinkProps<MouLinkType>>;
  showMyI9?: boolean;
}

const CompanyAccess = ({
  accountData,
  linkToMou = undefined,
  linkToMyI9TOS = undefined,
  showMyI9 = false,
}: CompanyAccessProps): JSX.Element => {
  const { user } = useAuthState();
  // eslint-disable-next-line camelcase
  const loggedInAccount = user?.account_id as string;

  const canVerifyOwn = accountData?.selfVerification;
  const canVerifyForClients = accountData?.type === AccountType.AGENCY;
  const canManageChildCompanies = accountData?.type === AccountType.CORPORATE;
  const isManaged =
    [AccountType.CHILD, AccountType.CLIENT].includes(accountData?.type as AccountType) &&
    loggedInAccount !== accountData?.id;
  const canUseWebServices = accountData?.webServiceAccess.enabled;

  const myI9Enabled: boolean = accountData?.myI9?.enabled ?? false;

  const pageTitle = 'Company Access';

  const companyName = (): string => {
    if (isManaged) {
      return accountData?.companyInfo.companyName ?? 'This Company';
    }
    return 'My Company';
  };

  const AccessField = (): JSX.Element => {
    return (
      <FieldGroup.Field className="tablet:grid-col-6" name={`${companyName()} is configured to:`}>
        {[
          canVerifyOwn ? 'Verify Its Own Employees' : null,
          isManaged ? 'Have Employees Managed and Verified by My Company' : null,
          canVerifyForClients ? 'Manage and Verify Employees for Clients' : null,
          canManageChildCompanies ? 'Manage Child Companies' : null,
          canUseWebServices ? 'Use Web Services' : null,
        ].filter(Boolean)}
      </FieldGroup.Field>
    );
  };

  return (
    <Section title={pageTitle} data-testid="company-access" className="company-access">
      <FieldGroup className="grid-row grid-gap">
        <AccessField />
        <>
          {linkToMou && <CompanyAccessMOU accountData={accountData} linkToMou={linkToMou} />}
          {showMyI9 && myI9Enabled && (
            <CompanyAccessMyI9 accountData={accountData} linkToMyI9TOS={linkToMyI9TOS} companyName={companyName} />
          )}
        </>
      </FieldGroup>
    </Section>
  );
};

export default CompanyAccess;
